/**
 * The service worker registration, held for the deployment watcher
 * (plan/portal-final/INTERFACES.md section 8).
 *
 * vite.config.ts builds `sw.js` with `skipWaiting` and `clientsClaim`, so a
 * new worker activates and takes over as soon as it installs, while the page
 * keeps running the old bundle until the watcher reloads it under the busy
 * rule. The worker is registered here rather than through
 * `virtual:pwa-register`: that helper calls `window.location.reload()` itself
 * when an updated worker takes control (on activation in `autoUpdate` mode,
 * and on any update found more than a minute after load in `prompt` mode),
 * which would discard frames an operator has not queued yet.
 */

export const WORKER_WAIT_TIMEOUT_MS = 10_000;

let registration: ServiceWorkerRegistration | null = null;

export function setServiceWorkerRegistration(next: ServiceWorkerRegistration | null | undefined): void {
  registration = next ?? null;
}

/** Registers `sw.js` once from main.tsx. A no-op on the dev server, which serves no worker. */
export function registerServiceWorker(): void {
  if (import.meta.env.DEV || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const base = import.meta.env.BASE_URL;
  navigator.serviceWorker
    .register(`${base}sw.js`, { scope: base })
    .then(setServiceWorkerRegistration)
    .catch(() => {
      // No worker means no offline shell; the app and its IndexedDB queue still run.
    });
}

/** Asks the browser to fetch `sw.js` again; a no-op before registration and when offline. */
export function updateServiceWorker(): void {
  registration?.update().catch(() => {
    // Offline or the worker script failed to load; the next trigger retries.
  });
}

/**
 * Before a reload: fetch the worker once more and, when a new one is
 * installing, wait until it activates (or `timeoutMs` passes), so the reload
 * is served the new precache rather than the old shell.
 */
export async function waitForNewWorker(timeoutMs: number = WORKER_WAIT_TIMEOUT_MS): Promise<void> {
  const current = registration;
  if (!current) return;
  try {
    await current.update();
  } catch {
    return;
  }
  const pending = current.installing ?? current.waiting;
  if (!pending) return;
  await new Promise<void>((resolve) => {
    const done = () => {
      clearTimeout(timer);
      pending.removeEventListener('statechange', onState);
      resolve();
    };
    const onState = () => {
      if (pending.state === 'activated' || pending.state === 'redundant') done();
    };
    const timer = setTimeout(done, timeoutMs);
    pending.addEventListener('statechange', onState);
  });
}
